import React from 'react';
import { PipelineMetrics } from '../../types/pipeline';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface PipelineMetricsProps {
  metrics: PipelineMetrics;
}

const PipelineMetricsComponent: React.FC<PipelineMetricsProps> = ({ metrics }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Összes érték */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Összes érték</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metrics.totalValue.toLocaleString('hu-HU')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{metrics.currency}</p>
          </div>
          <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
            <CurrencyDollarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </div>

      {/* Összes dokumentum */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Összes dokumentum</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metrics.totalCount}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">db</p>
          </div>
          <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
            <DocumentTextIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
        </div>
      </div>

      {/* Konverziós ráta */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Konverziós ráta</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metrics.conversionRate.overall.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ajánlat → Számla</p>
          </div>
          <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
            <ArrowTrendingUpIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
        </div>
      </div>

      {/* Átlagos üzlet méret */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Átlagos érték</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metrics.averageValue.toLocaleString('hu-HU')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{metrics.currency}</p>
          </div>
          <div className="bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg">
            <ClockIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
      </div>

      {/* Részletes konverziós ráták */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:col-span-2 lg:col-span-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Konverziós ráták részletesen</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Ajánlat → Megrendelés</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {metrics.conversionRate.quotationToOrder.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: `${Math.min(metrics.conversionRate.quotationToOrder, 100)}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Megrendelés → Szállítás</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {metrics.conversionRate.orderToDelivery.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full"
                style={{ width: `${Math.min(metrics.conversionRate.orderToDelivery, 100)}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Szállítás → Számla</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {metrics.conversionRate.deliveryToInvoice.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${Math.min(metrics.conversionRate.deliveryToInvoice, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineMetricsComponent;

// Named export for consistency
export { PipelineMetricsComponent as PipelineMetrics };
